import React, { useState, memo } from 'react';
import { ArrowLeft, Wand2, Share2, Trash2, Calendar, Clock, Play, Edit, Tag, User, Download, Heart, MessageCircle } from 'lucide-react';
import { Breadcrumbs } from './Breadcrumbs';
import { ConfirmDialog } from './ConfirmDialog';
type Story = {
  id: number;
  thumbnail: string;
  duration: string;
  title: string;
  description: string;
  author: string;
  date: string;
  tags?: string[];
  transcript?: string;
  summary?: string;
};
// Story data matching StoriesPage
const storiesData: Story[] = [{
  id: 1,
  thumbnail: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&h=600&fit=crop',
  duration: '12:45',
  title: 'How Mom and Dad Met',
  description: 'The story of how my parents met at a college dance in 1985, their first date at the diner, and the moment Dad knew she was the one.',
  author: 'Sarah Mitchell',
  date: 'December 18, 2024',
  tags: ['Family', 'Love Story', 'Memories'],
  summary: "A heartwarming tale of young love that began at a college dance in 1985. Sarah shares the story of her parents' first meeting, their nervous first date at the local diner, and the magical moment when her father realized he had found his soulmate. This story captures the innocence and excitement of falling in love in a simpler time.",
  transcript: 'So, my parents met in 1985 at a college dance. My mom was a sophomore studying education, and my dad was a junior in engineering. He saw her across the room and said he just knew. He spent the whole night working up the courage to ask her to dance...'
}, {
  id: 2,
  thumbnail: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
  duration: '8:23',
  title: "Grandma's Apple Pie Recipe",
  description: "Learning to make Grandma Rose's famous apple pie from scratch. She shares the secret ingredient and memories of baking with her own mother.",
  author: 'Emily Chen',
  date: 'December 15, 2024',
  tags: ['Recipes', 'Family', 'Traditions'],
  summary: "Emily documents the process of learning her grandmother's legendary apple pie recipe, passed down through four generations. Grandma Rose reveals the secret ingredient that makes her pie special and shares touching memories of baking with her own mother during the Great Depression.",
  transcript: "Okay, so first you need six large Granny Smith apples. Grandma always said the tartness is what makes it special. Now, here's the secret - you add just a tablespoon of apple cider vinegar to the filling..."
}, {
  id: 3,
  thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop',
  duration: '15:30',
  title: 'Road Trip to Yellowstone',
  description: "Our family's unforgettable road trip through Yellowstone National Park. Bears, geysers, and the time we got lost trying to find Old Faithful.",
  author: 'Michael Rodriguez',
  date: 'December 10, 2024',
  tags: ['Travel', 'Adventure', 'Family'],
  summary: "An epic family adventure through America's first national park. Michael recounts the excitement of seeing wild bears, the majesty of Old Faithful, and the hilarious mishap when they got completely lost using an old paper map. A story filled with laughter, wonder, and family bonding.",
  transcript: 'We left early in the morning, packed the minivan with snacks and camping gear. The kids were so excited they barely slept the night before. When we finally saw our first buffalo, everyone went silent in awe...'
}, {
  id: 4,
  thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
  duration: '10:15',
  title: 'Summer at Lake Tahoe',
  description: 'Memories of our childhood summers at the lake house - fishing with Grandpa, swimming until sunset, and campfire stories under the stars.',
  author: 'James Patterson',
  date: 'December 5, 2024',
  tags: ['Childhood', 'Summer', 'Memories'],
  summary: 'James reminisces about magical childhood summers spent at the family lake house in Tahoe. Early morning fishing trips with Grandpa, swimming competitions with cousins, and gathering around the campfire for ghost stories. These summers shaped who he became and created bonds that last a lifetime.',
  transcript: "Every summer, we'd pile into the car and drive up to Tahoe. The lake house had been in our family for three generations. I can still smell the pine trees and hear the water lapping against the dock..."
}, {
  id: 5,
  thumbnail: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&h=600&fit=crop',
  duration: '6:42',
  title: 'The Day We Adopted Max',
  description: 'How our golden retriever Max became part of the family. From the shelter to his first day home, and all the chaos and love that followed.',
  author: 'Lisa Thompson',
  date: 'November 28, 2024',
  tags: ['Pets', 'Family', 'Memories'],
  summary: 'The heartwarming story of adopting Max, a golden retriever who had been waiting at the shelter for months. Lisa describes the instant connection they felt, the chaotic first day home, and how Max quickly became the most beloved member of the family.',
  transcript: 'We walked into the shelter just to look, but when we saw Max, something just clicked. He was sitting quietly in his kennel, and when he looked up at us with those big brown eyes, we knew he was coming home with us...'
}, {
  id: 6,
  thumbnail: 'https://images.unsplash.com/photo-1464207687429-7505649dae38?w=800&h=600&fit=crop',
  duration: '18:20',
  title: "Dad's Navy Stories",
  description: 'Dad shares his experiences serving in the Navy during the 1970s. Tales of life at sea, ports around the world, and the friendships that lasted a lifetime.',
  author: 'Robert Williams',
  date: 'November 22, 2024',
  tags: ['Military', 'History', 'Family'],
  summary: "Robert's father shares vivid memories from his Navy service in the 1970s. From the challenges of life aboard ship to exotic ports in Asia and Europe, these stories capture the adventure, camaraderie, and personal growth that came from serving his country.",
  transcript: "I enlisted right out of high school in 1972. Boot camp was tough, but it made me who I am today. My first deployment took us across the Pacific. I'd never been more than 50 miles from home before that..."
}, {
  id: 7,
  thumbnail: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=800&h=600&fit=crop',
  duration: '9:55',
  title: 'Christmas Traditions',
  description: "Our family's Christmas traditions passed down through generations - from decorating the tree to Mom's famous sugar cookies and midnight mass.",
  author: 'Maria Garcia',
  date: 'November 18, 2024',
  tags: ['Traditions', 'Holidays', 'Family'],
  summary: 'Maria shares the rich tapestry of Christmas traditions that have been passed down through her family for generations. From the specific way they decorate the tree to the secret recipe for sugar cookies, each tradition carries deep meaning and connects the family across time.',
  transcript: 'Christmas Eve always starts the same way - we wake up early and Mom starts making her famous sugar cookies. The recipe came from her grandmother in Mexico. While the cookies bake, we decorate the tree together...'
}, {
  id: 8,
  thumbnail: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop',
  duration: '14:10',
  title: 'Growing Up in Brooklyn',
  description: 'Stories from my childhood in 1960s Brooklyn - stickball in the streets, corner stores, and the tight-knit neighborhood where everyone knew your name.',
  author: 'Frank Russo',
  date: 'November 12, 2024',
  tags: ['Childhood', 'History', 'Memories'],
  summary: 'Frank paints a vivid picture of growing up in 1960s Brooklyn. A time when kids played stickball in the streets until dark, when you could walk to the corner store alone, and when the whole neighborhood felt like one big family. A nostalgic look at a bygone era.',
  transcript: "Brooklyn in the 60s was a different world. Every morning, I'd walk to school with the same group of kids. We'd stop at Mr. Goldstein's corner store for penny candy. After school, we'd play stickball until our mothers called us in for dinner..."
}];
type StoryDetailPageProps = {
  storyId: number;
  onBack: () => void;
  onShowToast?: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  onCreateStorybook?: (storyId: number) => void;
};
export function StoryDetailPage({
  storyId,
  onBack,
  onShowToast,
  onCreateStorybook
}: StoryDetailPageProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript'>('summary');
  // Find the story by ID
  const story = storiesData.find(s => s.id === storyId) || storiesData[0];
  const handleDelete = () => {
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    onShowToast?.('Story deleted successfully', 'success');
    setShowDeleteDialog(false);
    onBack();
  };
  const handleCreateStorybook = () => {
    if (onCreateStorybook) {
      onCreateStorybook(storyId);
    }
  };
  return <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" data-id="element-2510">
      <div className="px-8 py-6" data-id="element-2511">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[{
        label: 'Stories',
        onClick: onBack
      }, {
        label: story.title
      }]} data-id="element-2512" />

        {/* Hero Section with Video */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8" data-id="element-2513">
          {/* Left: Video Player */}
          <div className="lg:col-span-2" data-id="element-2514">
            <div className="bg-black rounded-2xl overflow-hidden aspect-video shadow-2xl" data-id="element-2515">
              <div className="relative w-full h-full" data-id="element-2516">
                <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover" data-id="element-2517" />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-2518">
                  <button className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-2xl" data-id="element-2519">
                    <Play className="w-10 h-10 text-gray-900 ml-2" data-id="element-2520" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Story Info Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm h-fit" data-id="element-2521">
            <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2522">
              {story.title}
            </h1>

            <div className="space-y-3 mb-6" data-id="element-2523">
              <div className="flex items-center gap-3 text-gray-600" data-id="element-2524">
                <User className="w-5 h-5" data-id="element-2525" />
                <span data-id="element-2526">{story.author}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600" data-id="element-2527">
                <Calendar className="w-5 h-5" data-id="element-2528" />
                <span data-id="element-2529">{story.date}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600" data-id="element-2530">
                <Clock className="w-5 h-5" data-id="element-2531" />
                <span data-id="element-2532">{story.duration}</span>
              </div>
            </div>

            {/* Tags */}
            {story.tags && story.tags.length > 0 && <div className="mb-6" data-id="element-2533">
                <div className="flex flex-wrap gap-2" data-id="element-2534">
                  {story.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-lg text-sm font-medium" data-id="element-2535">
                      <Tag className="w-3 h-3" data-id="element-2536" />
                      {tag}
                    </span>)}
                </div>
              </div>}

            {/* Action Buttons */}
            <div className="space-y-3" data-id="element-2537">
              <button onClick={handleCreateStorybook} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg" data-id="element-2538">
                <Wand2 className="w-5 h-5" data-id="element-2539" />
                Create Storybook
              </button>

              <div className="grid grid-cols-2 gap-3" data-id="element-2540">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2541">
                  <Share2 className="w-4 h-4" data-id="element-2542" />
                  Share
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2543">
                  <Download className="w-4 h-4" data-id="element-2544" />
                  Download
                </button>
              </div>

              <button onClick={handleDelete} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 transition-colors" data-id="element-2545">
                <Trash2 className="w-4 h-4" data-id="element-2546" />
                Delete Story
              </button>
            </div>
          </div>
        </div>

        {/* Content Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-2547">
          {/* Tab Headers */}
          <div className="border-b border-gray-200" data-id="element-2548">
            <div className="flex gap-1 px-6" data-id="element-2549">
              <button onClick={() => setActiveTab('summary')} className={`px-6 py-4 font-semibold border-b-2 transition-colors ${activeTab === 'summary' ? 'border-green-500 text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2550">
                Summary
              </button>
              <button onClick={() => setActiveTab('transcript')} className={`px-6 py-4 font-semibold border-b-2 transition-colors ${activeTab === 'transcript' ? 'border-green-500 text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2551">
                Transcript
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8" data-id="element-2552">
            {activeTab === 'summary' && <div data-id="element-2553">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2554">
                  Story Summary
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-2555">
                  {story.summary || story.description}
                </p>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6" data-id="element-2556">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-2557">
                    About this story
                  </h3>
                  <p className="text-gray-700" data-id="element-2558">{story.description}</p>
                </div>
              </div>}

            {activeTab === 'transcript' && <div data-id="element-2559">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2560">
                  Full Transcript
                </h2>
                <div className="prose prose-lg max-w-none" data-id="element-2561">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-id="element-2562">
                    {story.transcript || 'Transcript not available for this story.'}
                  </p>
                </div>
              </div>}
          </div>
        </div>

        {/* Engagement Section */}
        <div className="grid md:grid-cols-2 gap-6 mt-8" data-id="element-2563">
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2564">
            <div className="flex items-center gap-3 mb-4" data-id="element-2565">
              <Heart className="w-6 h-6 text-red-500" data-id="element-2566" />
              <h3 className="text-xl font-semibold text-gray-900" data-id="element-2567">Reactions</h3>
            </div>
            <p className="text-gray-600" data-id="element-2568">Be the first to react to this story</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2569">
            <div className="flex items-center gap-3 mb-4" data-id="element-2570">
              <MessageCircle className="w-6 h-6 text-blue-500" data-id="element-2571" />
              <h3 className="text-xl font-semibold text-gray-900" data-id="element-2572">Comments</h3>
            </div>
            <p className="text-gray-600" data-id="element-2573">
              No comments yet. Start the conversation!
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog isOpen={showDeleteDialog} title="Delete Story?" message="Are you sure you want to delete this story? This action cannot be undone." confirmText="Delete" cancelText="Cancel" variant="danger" onConfirm={confirmDelete} onCancel={() => setShowDeleteDialog(false)} data-id="element-2574" />
    </div>;
}